import { Alert, Button, Group, Loader, Modal, Stack, Text } from '@mantine/core'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { IconBuildingBank, IconExclamationMark } from '@tabler/icons-react'
import { useState } from 'react'

interface PaymentMethodUpdateModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
  clientSecret?: string | null
  isExistingMethod?: boolean
  isPolling?: boolean
}

export function PaymentMethodUpdateModal({
  opened,
  onClose,
  onSuccess,
  clientSecret,
  isExistingMethod = false,
  isPolling = false,
}: PaymentMethodUpdateModalProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [microdepositVerificationUrl, setMicrodepositVerificationUrl] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !clientSecret) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      // For Setup Intents (updating/adding payment methods without charging)
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/dashboard/billing`,
        },
        redirect: 'if_required',
      })

      if (error) {
        setErrorMessage(error.message || 'An unexpected error occurred.')
        return
      }

      // Stripe.js doesn't handle microdeposit verification; surface the hosted page ourselves.
      const nextAction = setupIntent?.next_action
      if (nextAction?.type === 'verify_with_microdeposits') {
        const hostedUrl = nextAction.verify_with_microdeposits?.hosted_verification_url
        if (hostedUrl) {
          setMicrodepositVerificationUrl(hostedUrl)
          return
        }
      }

      // Setup succeeded - payment method saved
      onSuccess()
    } catch (_err) {
      setErrorMessage('An unexpected error occurred.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (!isProcessing && !isPolling) {
      setMicrodepositVerificationUrl(null)
      onClose()
    }
  }

  const isFormDisabled = !stripe || !elements || !clientSecret || isProcessing || isPolling

  const getTitle = () => {
    if (isExistingMethod) {
      return 'Update Payment Method'
    }
    return 'Add Payment Method'
  }

  const getDescription = () => {
    if (isExistingMethod) {
      return 'Update your payment information below. This will not charge your card.'
    }
    return 'Add a new payment method to your account. This will not charge your card.'
  }

  const getButtonText = () => {
    if (isProcessing) {
      return isExistingMethod ? 'Updating...' : 'Adding...'
    }
    if (isPolling) {
      return 'Processing...'
    }
    return isExistingMethod ? 'Update Payment Method' : 'Add Payment Method'
  }

  // Show loading state while client secret is being generated
  if (!clientSecret) {
    return (
      <Modal opened={opened} onClose={handleCancel} title={getTitle()} size="md" centered>
        <Stack gap="xl" align="center" py="xl">
          <Stack gap="xs" align="center">
            <Text c="dimmed" ta="center" size="lg">
              Setting up payment method...
            </Text>
          </Stack>
          <Loader size="lg" />
        </Stack>
      </Modal>
    )
  }

  // Bank account added but pending microdeposit verification
  if (microdepositVerificationUrl) {
    return (
      <Modal opened={opened} onClose={handleCancel} title="Verify Your Bank Account" size="lg" centered>
        <Stack gap="lg">
          <Group gap="sm" align="flex-start">
            <IconBuildingBank size={32} color="var(--mantine-color-indigo-6)" />
            <Text size="sm">
              Your bank account was added, but it still needs to be verified before it can be used. Stripe will send a
              small deposit to your account in 1–2 business days. Once it arrives, use the link below to enter the
              verification code and finish setup.
            </Text>
          </Group>

          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={handleCancel} size="md">
              Close
            </Button>
            <Button
              component="a"
              href={microdepositVerificationUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="md"
              color="indigo">
              Verify Bank Account
            </Button>
          </Group>
        </Stack>
      </Modal>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={getTitle()}
      size="lg"
      centered
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}>
      <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          <Text c="dimmed" size="sm">
            {getDescription()}
          </Text>

          {/* Stripe Payment Element configured for Setup Intent */}
          <div style={{ minHeight: '200px' }}>
            <PaymentElement
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card', 'us_bank_account'],
                // For setup intents, we want to focus on saving the payment method
                fields: {
                  billingDetails: 'auto',
                },
              }}
            />
          </div>

          {/* Error Display */}
          {errorMessage && (
            <Alert variant="light" color="red" title="Setup Failed" icon={<IconExclamationMark />}>
              {errorMessage}
            </Alert>
          )}

          {/* Action Buttons */}
          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={handleCancel} disabled={isFormDisabled} size="md">
              Cancel
            </Button>
            <Button type="submit" disabled={isFormDisabled} size="md" color="indigo">
              {isProcessing || isPolling ? (
                <Group gap="xs">
                  <Loader size="xs" />
                  <span>{getButtonText()}</span>
                </Group>
              ) : (
                getButtonText()
              )}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
